module Meevo.Plugins.Tippy {

    export enum TippyMode {
        Disabled,
        Offline,
        Kiosk
    }

    interface IEmployeeTipChargeAmounts { [key: string]: { chargeAmount: number, emailAddress?: string } };

    interface IPartialRefundResponse {
        data: {
            tipLines: {
                employeeId: string,
                convenienceFee: number
                transactionId: string
            }[]
        }
    }

    export interface IUnauthorizedConfig {
        additionalMessage?: string,
        modalType?: Meevo.Components.Modal.ModalTypeEnum
    }

    export class TippyService {
        registerContext: Register.RegisterContext;
        myTimeout: any;
        kiosk: Kiosk;
        // Only the kiosk-lock checks (isPairedKioskLocked/isKioskLocked) pass this - other Tippy
        // microservice calls (getTippyMode, addKiosk, employee mappings, log data, etc.) are
        // untimed, unaffected by this value.
        private readonly lockCheckTimeoutMs = 5000;

        constructor(
            private meevoDataService: Meevo.Components.Common.DataService,
            private dataService: Meevo.Components.Common.TippyDataService,
            private $q: ng.IQService,
            private modalService: Meevo.Components.Modal.IModalService,
            private pushMessageHandlerService: Meevo.Components.PushMessageHandlerService,
            private securityService: Meevo.Components.Common.ISecurityService,
            private roundingService: Meevo.Components.Common.IRoundingService,
            private $translate: angular.translate.ITranslateService,
            private identityService: Meevo.Components.Common.IIdentityService,
            private appSettings: Meevo.Components.Common.IAppSettings
        ) {
        }

        public getTippyMode(drawerId: string): ng.IPromise<TippyMode> {
            return this.checkModule().then(isModuleEnabled => {
                if (isModuleEnabled) {
                    let promises = [
                        this.dataService.getSection("", "config"),
                        this.dataService.getKioskByDrawerId(drawerId)
                    ];

                    return this.$q.all(promises).then(results => {
                        this.kiosk = results[1];

                        if (results[0].isTipProcessingEnabled && !results[1]) {
                            return TippyMode.Offline;
                        }

                        return results[0].isTipProcessingEnabled && !!results[1] ?
                            TippyMode.Kiosk :
                            TippyMode.Disabled;
                    })
                }
                else {
                    return TippyMode.Disabled;
                }
            });
        }

        createTransaction(registerContext: Register.RegisterContext) {
            registerContext.sale.externalTipEmployeeIds = [];
            let tipLines = _(registerContext.sale.tipLines).where({ isRefund: false });
            if (tipLines.length === 0 ||
                registerContext.registerTicketService.isPastTransactionMode) {

                return this.$q.resolve();
            }

            const tipEmployeeIds = _(registerContext.sale.tipLines).pluck('employeeId');
            this.registerContext = registerContext;

            const sendTransactionCreate = () => {
                let employeeChargeAmounts = this.getEmployeeTipChargeAmounts();
                let request = {
                    tipLines: this.getTipLines(employeeChargeAmounts),
                    clients: this.getClients(),
                    isFundedAtSettlementTime: registerContext.hasMeevoPaySplitFunding()
                };

                this.setTippyPurchasesAndTaxes(registerContext.sale, employeeChargeAmounts, request);

                // Block the UI until the kiosk responds and the waiting modal takes over, so the payment
                // options screen that finishSection switches to can't be interacted with while the kiosk
                // request is still in flight.
                let busyStopped = false;
                const stopBusy = () => {
                    if (!busyStopped) {
                        busyStopped = true;
                        registerContext.busyService.stop();
                    }
                };
                registerContext.busyService.start();

                return this.dataService.customMethodData(`kiosk/${this.kiosk.deviceId}/transaction?enableUnauthorizedHandling=true`, "POST", request)
                    .then((response) => {
                        stopBusy();

                        if (!response.data) {
                            return;
                        }

                        if (!response.data.transactionId && response.data.isKioskLocked) {
                            this.modalService.alert('MESSAGE_TIPPY_KIOSK_LOCKED', Components.Modal.ModalTranslationModes.Translate);
                            return this.$q.reject();
                        }

                        this.setTransactionTimer();

                        this.registerContext.sale.externalTipTransactionId = response.data.transactionId;

                        let options: Meevo.Components.Modal.IModalOptions = {
                            templateUrl: "app/plugins/register/modals/tippyBusyModal.template.html",
                            controller: "meevo.plugins.register.TippyBusyModal",
                            controllerAs: "vm",
                            bindToController: true
                        }

                        return this.modalService.showCustom(options).catch(() => {
                            this.cancelTransaction();
                            registerContext.selectSection(Register.RegisterSectionTypeEnum.Services);
                            return this.$q.reject();
                        });
                    })
                    .catch((error) => {
                        stopBusy();

                        if (!error.data || !error.data.skipInterceptor) {
                            return this.$q.reject(error.data.message);
                        }
                        if (error.data.customState && error.data.customState.isUnauthorized) {
                            let message = this.getCustomErrorMessage(error.data.message, unauthorizedConfig);
                            this.modalService.alert(message, Components.Modal.ModalTranslationModes.AsHtml);
                        }
                        else {
                            const modalOptions = {
                                okButtonContentKey: "LABEL_RETRY",
                                cancelButtonContentKey: "LABEL_CONTINUE",
                                showCancelButton: true
                            };
                            this.modalService.yellowConfirm(error.data.message,
                                Components.Modal.ModalTranslationModes.Translate, true, modalOptions
                            ).then(() => {
                                return this.createTransaction(registerContext);
                            }).catch(() => {
                                return this.$q.resolve();
                            });
                        }
                    });
            };

            let unauthorizedConfig: IUnauthorizedConfig = {
                additionalMessage: "MESSAGE_TIPPY_UNAUTHORIZED_CREATE_TRANSACTION"
            }
            return this.isTippyKioskMode(registerContext.selectedDrawer.id, tipEmployeeIds, unauthorizedConfig).then(isKioskMode => {
                if (!isKioskMode) {
                    return;
                }

                return this.isPairedTerminalLocked(registerContext).then(isLocked => {
                    if (isLocked) {
                        registerContext.showTerminalLockedAlert();
                        return this.$q.reject();
                    }

                    const employeeIds = _(registerContext.sale.tipLines)
                        .filter(tipLine => tipLine.employeeId !== '00000000-0000-0000-0000-000000000000')
                        .filter(tipLine => tipEmployeeIds.indexOf(tipLine.employeeId) != -1)
                        .filter(tipLine => tipLine.employee && !tipLine.employee.emailAddress)
                        .map(tipLine => tipLine.employee.id);

                    if (employeeIds.length === 0) {
                        return sendTransactionCreate();
                    }

                    const employeeRequest = {
                        employeeIds: employeeIds
                    };

                    return this.meevoDataService.customMethodData('api/employee/byIds/list', "POST", employeeRequest)
                        .then((response) => {
                            const employees = response && response.data as Meevo.Model.EmployeeListItem[];
                            if (!employees) {
                                return;
                            }
                            for (const employee of employees) {
                                const tipLine = registerContext.sale.tipLines.find(tipLine => tipLine.employeeId === employee.id);
                                if (tipLine) {
                                    tipLine.employee.emailAddress = employee.emailAddress;
                                }
                            }
                        })
                        .finally(() => {
                            return sendTransactionCreate();
                        });
                });
            });
        }

        // If the kiosk device is paired (as the SFO1's TippyKioskId) with the register's own MeevoPay terminal,
        // and that terminal is currently locked for CC processing, block the kiosk request instead of interrupting it.
        // On any failure to check, fail open and allow the kiosk request to proceed as today.
        private isPairedTerminalLocked(registerContext: Register.RegisterContext): ng.IPromise<boolean> {
            if (!registerContext.enableDualLockBehavior) {
                return this.$q.resolve(false);
            }

            const terminal = registerContext.selectedTerminal;
            if (!terminal || !terminal.tippyKioskId || !this.kiosk ||
                terminal.tippyKioskId.toUpperCase() !== (this.kiosk.deviceId || "").toUpperCase()) {
                return this.$q.resolve(false);
            }

            const request = { 
                id: terminal.id, 
                merchantAccountId: terminal.merchantAccountId 
            };

            return registerContext.dataService.customMethodData(`api/MerchantTerminal/${terminal.id}`, "POST", request)
                .then((response: any) => !!(response && response.data && response.data.hasLockForProcessing))
                .catch(() => false);
        }

        private getTipLines(employeeChargeAmounts: IEmployeeTipChargeAmounts) {
            let tipLines = this.registerContext.sale.tipLines
                .filter((tipLine) => !tipLine.isRefund)
                .map((tipLine) => {
                    return {
                        employeeId: tipLine.employeeId,
                        amount: tipLine.amount,
                        serviceTotal: tipLine.serviceTotal,
                        prepaidTipMinimum: tipLine.prepaidTipMinimum,
                        isRefund: tipLine.isRefund,
                        employeeEmailAddress: tipLine.employee ? tipLine.employee.emailAddress : undefined,
                    }
                });

            // package prepaid tip is not sent to MeevoTips
            for (const tipLine of tipLines) {
                tipLine.amount = this.registerContext.subtractNormalizedNumbers(tipLine.amount, tipLine.prepaidTipMinimum);
                tipLine.prepaidTipMinimum = 0;
            }

            for (const employeeId in employeeChargeAmounts) {
                if (employeeChargeAmounts.hasOwnProperty(employeeId)) {
                    let existingTipLine = _(tipLines).findWhere({ employeeId: employeeId, isRefund: false });
                    if (!!existingTipLine) {
                        const chargeAmount = employeeChargeAmounts[employeeId].chargeAmount;
                        existingTipLine.amount = this.registerContext.addNormalizedNumbers(existingTipLine.amount, chargeAmount);
                        existingTipLine.prepaidTipMinimum = this.registerContext.addNormalizedNumbers(existingTipLine.prepaidTipMinimum, chargeAmount);
                        existingTipLine.serviceTotal = this.registerContext.subtractNormalizedNumbers(existingTipLine.serviceTotal, chargeAmount);
                    }
                    else {
                        tipLines.push({
                            employeeId: employeeId,
                            amount: employeeChargeAmounts[employeeId].chargeAmount,
                            prepaidTipMinimum: employeeChargeAmounts[employeeId].chargeAmount,
                            serviceTotal: 0,
                            isRefund: false,
                            employeeEmailAddress: employeeChargeAmounts[employeeId].emailAddress
                        })
                    }
                }
            }

            return tipLines;
        }

        private getClients() {
            let saleLines: any[] = [...this.registerContext.sale.saleLines];
            // if there's just tip lines but no sale lines, need to add dummy sale line so client is sent to tippy ms
            if (saleLines.length === 0 && this.registerContext.sale.tipLines.length > 0 &&
                this.registerContext.allUniqueClients.length > 0) {
                const firstClient = this.registerContext.allUniqueClients[0];
                saleLines.push({
                    clientId: firstClient.getId(),
                    client: firstClient.client
                });
            }
            return _(saleLines)
                .map((saleLine) => {
                    return {
                        employeeId: saleLine.employeeId,
                        clientId: saleLine.clientId,
                        firstName: !!saleLine.client ? saleLine.client.firstName : "",
                        lastName: !!saleLine.client ? saleLine.client.lastName : ""
                    }
                });
        }

        confirmTransaction(sale: Model.Sale) {
            if (!sale.externalTipTransactionId) {
                return;
            }

            const referencePaymentLine = sale.paymentLines && sale.paymentLines.find(p =>
                !!(p.amount > 0 && p.paymentRequest && p.paymentRequest.isAuthorization && p.paymentRequest.processorTransactionId));
            const reference = (referencePaymentLine && referencePaymentLine.paymentRequest) ? referencePaymentLine.paymentRequest.processorTransactionId : undefined;
            this.dataService.customMethodData(`transaction/${sale.externalTipTransactionId}/confirm`, "POST",
                { meevoTransactionId: sale.transactionId, pspReference: reference });
            sale.externalTipTransactionId = null;
        }

        voidTransaction(externalTipTransactionId: string) {
            if (!externalTipTransactionId) {
                return;
            }

            this.dataService.customMethodData(`transaction/${externalTipTransactionId}/refund`, "POST", {});
        }

        refundTransaction(sale: Model.Sale, registerContext: Register.RegisterContext): ng.IPromise<boolean> {
            const employees: { amount: number, convenienceFee: number, employeeId: string, externalTipTransactionId: string }[] = [];

            for (let saleLine of sale.saleLines.filter(saleLine => !!saleLine.externalTipTransactionId && saleLine.isRefund)) {
                for (let charge of saleLine.charges.filter(charge => charge.chargeType == Model.ChargeTypeEnum.EmployeeTip)) {
                    for (let employeeCharge of this.getSaleLineEmployeeCharges(saleLine, charge.finalAmount)) {
                        employees.push({
                            amount: employeeCharge.chargeAmount,
                            convenienceFee: 0,
                            employeeId: employeeCharge.employeeId,
                            externalTipTransactionId: saleLine.externalTipTransactionId
                        });
                    }
                }
            }

            employees.push(...sale.tipLines.filter(tipLine => !!tipLine.externalTipTransactionId && tipLine.isRefund).
                map(tipLine => {
                    return {
                        amount: registerContext.subtractNormalizedNumbers(tipLine.amount, tipLine.prepaidTipMinimum),
                        convenienceFee: tipLine.convenienceFee,
                        employeeId: tipLine.employeeId,
                        externalTipTransactionId: tipLine.externalTipTransactionId
                    }
                }));

            if (employees.length === 0) {
                return this.$q.resolve(true);
            }

            const externalTipTransactionId = employees[0].externalTipTransactionId;
            const request = {
                meevoTransactionId: sale.transactionId,
                employees
            }
            
            this.dataService.customMethodData(`transaction/${externalTipTransactionId}/refund`, "POST", request);
            return this.dataService.customMethodData(`transaction/${externalTipTransactionId}`, "GET", {}).then((response) => {
                return response.data.isFundedAtSettlementTime;
            });
        }

        partialRefundTransaction(registerContext: Register.RegisterContext): ng.IPromise<any> {
            let refundExternalTipLines = registerContext.sale.tipLines.filter(tipLine => {
                return tipLine.isRefund &&
                    !!tipLine.externalTipTransactionId
            });

            registerContext.sale.tipConvenienceFeeRefund = 0;
            if (refundExternalTipLines.length === 0) {
                registerContext.updateTip();
                return this.$q.resolve();
            }

            let refundExternalTipTransactionIds = _(refundExternalTipLines).chain()
                .pluck("externalTipTransactionId")
                .uniq()
                .value();

            registerContext.busyService.start();

            return this.dataService.customMethodData(`refundInfo?transactionIds=${refundExternalTipTransactionIds}`, "GET", {}).then((response: IPartialRefundResponse) => {
                for (let refundTipLine of refundExternalTipLines) {
                    let matchingTransTipLine = response.data.tipLines.find(responseTipLine => {
                        return responseTipLine.employeeId === refundTipLine.employeeId &&
                            responseTipLine.transactionId === refundTipLine.externalTipTransactionId
                    });

                    if (!!matchingTransTipLine) {
                        refundTipLine.convenienceFee = matchingTransTipLine.convenienceFee;
                    }
                }
                this.updateTipConvenienceFee(registerContext.sale, registerContext, registerContext.sale.tipConvenienceFee);
            }).finally(() => {
                registerContext.busyService.stop();
                registerContext.updateTip();
            });
        }

        deleteTransaction(sale: Model.Sale) {
            if (!sale || !sale.externalTipTransactionId) {
                return;
            }

            this.dataService.customMethodData(`transaction/${sale.externalTipTransactionId}`, "DELETE", {});
            sale.externalTipTransactionId = null;
        }

        tipsSelected(transactionId) {
            if (this.registerContext.sale.externalTipTransactionId !== transactionId) {
                return;
            }
            this.getTransaction()
                .then(transaction => {
                    if (transaction.state === TransactionStateEnum.Canceled) {
                        // cancellation cleanup already occurred
                        return;
                    }
                    this.cleanupTransaction();
                });
        }

        private setTransactionTimer() {
            if (this.pushMessageHandlerService.isOpen) {
                return;
            }

            this.myTimeout = setTimeout(() => {
                this.getTransaction().then(transaction => {
                    if (transaction.state === TransactionStateEnum.Initial) {
                        this.setTransactionTimer();
                    }
            }) }, window["TippyPollingInterval"]);
        }

        private getTransaction(cleanup = true) : ng.IPromise<Transaction> {
            return this.dataService.customMethodData(`transaction/${this.registerContext.sale.externalTipTransactionId}`, "GET", {}).then((response) => {
                let transaction : Transaction = response.data;

                if (!transaction || transaction.state === TransactionStateEnum.Canceled) {
                    // transaction was externally cancelled
                    this.cleanupTransaction(true);
                    return transaction;
                }

                if (transaction.state === TransactionStateEnum.Pending) {
                    let employeeChargeAmounts = this.getEmployeeTipChargeAmounts();

                    for (let tipLine of transaction.tipLines) {
                        if (tipLine.amount > 0) {
                            this.registerContext.sale.externalTipEmployeeIds.push(tipLine.employeeId);
                        }
                        let matchingSaleTipLine = this.registerContext.sale.tipLines.find(saleTipLine => {
                            return saleTipLine.employeeId === tipLine.employeeId &&
                                saleTipLine.isRefund === false;
                        })
                        if (matchingSaleTipLine) {
                            matchingSaleTipLine.externalAmount = tipLine.amount;
                            // reset tipline amount to MeevoTips amount minus employee tip charge amount
                            matchingSaleTipLine.amount = this.registerContext.subtractNormalizedNumbers(tipLine.amount, 
                                employeeChargeAmounts[tipLine.employeeId] ? employeeChargeAmounts[tipLine.employeeId].chargeAmount : 0);
                            // add package prepaid tip back to tipline amount
                            matchingSaleTipLine.amount = this.registerContext.addNormalizedNumbers(matchingSaleTipLine.amount, matchingSaleTipLine.prepaidTipMinimum);
                            if (tipLine.amount > 0) {
                                matchingSaleTipLine.externalTipTransactionId = this.registerContext.sale.externalTipTransactionId;
                            }
                            this.registerContext.sale.tipPercentage = 0;
                            matchingSaleTipLine.percentage = 0;
                        }
                    }

                    this.updateTipConvenienceFee(this.registerContext.sale, this.registerContext, response.data.convenienceFee);
                    this.registerContext.sale.tipAmount = undefined;
                    return this.registerContext.recalculateSale().then(() => {
                        return transaction;
                    }).finally(() => {
                        if (cleanup) {
                            this.cleanupTransaction();
                        }
                    });
                }
                else {
                    return transaction;
                }
            });
        }

        // Splits an EmployeeTip charge across the primary and (optional) secondary employee. Shared so the math stays in one place.
        private getSaleLineEmployeeCharges(saleLine: Model.SaleLine, chargeAmount: number): { employeeId: string, chargeAmount: number }[] {
            // != null (not !!) so a 0% primary split still splits.
            if (!!saleLine.secondEmployeeId && saleLine.employeeSplitCommission != null) {
                // 50/50 only when both are full commission.
                const fullCommission = saleLine.employeeSplitCommission === 1 && saleLine.secondEmployeeSplitCommission === 1;
                const firstEmployeeChargePercentage = fullCommission ? .5 : saleLine.employeeSplitCommission;
                const firstEmployeeCharge = this.roundingService.multiplyNormalizedNumbers(chargeAmount, firstEmployeeChargePercentage);
                const secondEmployeeCharge = this.roundingService.subtractNormalizedNumbers(chargeAmount, firstEmployeeCharge);
                return [
                    { employeeId: saleLine.employeeId, chargeAmount: firstEmployeeCharge },
                    { employeeId: saleLine.secondEmployeeId, chargeAmount: secondEmployeeCharge }
                ];
            }

            return [{ employeeId: saleLine.employeeId, chargeAmount: chargeAmount }];
        }

        private getEmployeeTipChargeAmounts(): IEmployeeTipChargeAmounts {
            const saleLinesAndCharges = _(this.registerContext.sale.saleLines).chain()
                .map(saleLine => {
                    return {
                        saleLine,
                        chargeAmount: +_(saleLine.charges).chain()
                            .filter(charge => charge.chargeType === Model.ChargeTypeEnum.EmployeeTip)
                            .pluck("finalAmount")
                            .reduce((sum, finalAmount) => this.registerContext.addNormalizedNumbers(+sum, finalAmount))
                            .value()
                    }
                })
                .filter(saleLineAndCharges => saleLineAndCharges.chargeAmount > 0)
                // An employee tip service charge on a sale line that does not collect tips shows on
                // the Smart Receipt, but must not pull that employee into the MeevoTips kiosk.
                // NOTE: this function feeds BOTH directions - getTipLines adds the charge to the
                // outgoing amount, and the kiosk-return handler subtracts it again.  Filtering here
                // keeps the two symmetric; filtering only the outgoing call site would subtract a
                // charge that was never added and understate the employee's tip.
                .filter(saleLineAndCharges => {
                    const saleLine = saleLineAndCharges.saleLine;

                    if (saleLine.saleType === Model.SalesTypeEnum.Service) {
                        return !(saleLine.item && (<any>saleLine.item).doNotAllowTips);
                    }

                    if (saleLine.saleType === Model.SalesTypeEnum.Product) {
                        return this.registerContext.includeProductsInTips;
                    }

                    return true;
                })
                .value();
            const employeeChargeAmounts = {};

            let addEmployeeChargeAmount = (employeeId: string, chargeAmount: number, emailAddress?: string) => {
                if (employeeId === Guid.empty.toString()) {
                    return;
                }

                if (employeeChargeAmounts[employeeId] == null) {
                    employeeChargeAmounts[employeeId] = { chargeAmount: 0 };
                }

                let existChargeAmount = employeeChargeAmounts[employeeId].chargeAmount;
                employeeChargeAmounts[employeeId].chargeAmount = this.registerContext.addNormalizedNumbers(existChargeAmount, chargeAmount);
                employeeChargeAmounts[employeeId].emailAddress = emailAddress;
            }

            for (const saleLineAndCharges of saleLinesAndCharges) {
                let saleLine = saleLineAndCharges.saleLine;
                for (const employeeCharge of this.getSaleLineEmployeeCharges(saleLine, saleLineAndCharges.chargeAmount)) {
                    const employee = employeeCharge.employeeId === saleLine.secondEmployeeId ? saleLine.secondEmployee : saleLine.employee;
                    addEmployeeChargeAmount(employeeCharge.employeeId, employeeCharge.chargeAmount, employee ? employee.emailAddress : undefined);
                }
            }

            return employeeChargeAmounts;
        }

        private cleanupTransaction(cancel: boolean = false) {
            if (cancel) {
                this.modalService.cancel();
            } else {
                this.modalService.hide();
            }
            clearTimeout(this.myTimeout);
        }

        public unlockKiosk(drawerId: string): ng.IPromise<any> {
            return this.dataService.unlockKiosk(drawerId);
        }

        private cancelTransaction() {
            // Make one last attempt to get transaction
            this.getTransaction(false).then(transaction => {

                if (transaction && transaction.state === TransactionStateEnum.Initial) {
                    this.deleteTransaction(this.registerContext.sale);
                }
            })
            .finally(() => {
                this.cleanupTransaction();
            });
        }

        setTippyPurchasesAndTaxes(sale: Model.Sale, employeeChargeAmounts: IEmployeeTipChargeAmounts, request): void {
            let totalTipCharge = _(Object.keys(employeeChargeAmounts)).chain()
                .map(key => employeeChargeAmounts[key])
                .reduce((total, item) => this.registerContext.addNormalizedNumbers(total, item.chargeAmount), 0)
                .value();

            request.purchasesAndTaxes = this.registerContext.normalizeDecimalPlaces(sale.saleLines.map(saleLine => saleLine.finalPriceCharge)
                .reduce((total, next) => total += next, 0) + sale.tax - sale.taxIncluded - totalTipCharge);
            request.purchasesAndTaxesLabel = this.registerContext.$translate.instant('LABEL_PURCHASES_AND_TAXES');
        }

        public getTippyEmployeeMappings(unauthorizedConfig: IUnauthorizedConfig = undefined):ng.IPromise<any> {
            return this.fetchTippyEmployeeMappings(unauthorizedConfig).then(result => result.mappings);
        }

        // isAvailable is false when MeevoTips is not configured or the integration is unauthorized (the
        // mappings could not be read); an empty list from a successful call is still available.
        private fetchTippyEmployeeMappings(unauthorizedConfig: IUnauthorizedConfig = undefined): ng.IPromise<{ isAvailable: boolean, mappings: any[] }> {
            let defer = this.$q.defer<{ isAvailable: boolean, mappings: any[] }>();
            const unavailable = { isAvailable: false, mappings: [] };

            let unauthorizedHandler = (ex) => {
                if (unauthorizedConfig) {
                    let message = this.getCustomErrorMessage(ex.data.message, unauthorizedConfig);
                    let modalType = unauthorizedConfig.modalType || Components.Modal.ModalTypeEnum.Alert;
                    if (modalType === Components.Modal.ModalTypeEnum.Alert) {
                        this.modalService.alert(message, Components.Modal.ModalTranslationModes.AsHtml, true);
                        defer.resolve(unavailable);
                    }
                    else {
                        ex.message = message;
                        defer.reject(ex)
                    }
                }
                else {
                    defer.resolve(unavailable);
                }
            };

            this.isTippyConfigured().then(isConfigured => {
                if (!isConfigured) {
                    defer.resolve(unavailable);
                    return;
                }

                return this.dataService.customMethodData('Employees/List?enableUnauthorizedHandling=true', 'GET', null)
                    .then(res => {
                        defer.resolve({ isAvailable: true, mappings: res.data ? res.data.mappings || [] : [] });
                    })
                    .catch(ex => {
                        if (!ex.data || !ex.data.skipInterceptor) {
                            defer.reject(ex);
                            return;
                        }

                        unauthorizedHandler(ex);
                    });
            });

            return defer.promise;
        }

        public checkModule(): ng.IPromise<boolean> {
            if (this.identityService.inSandboxMode()) {
                return this.$q.resolve(false);
            }
            return this.securityService.checkModuleAccess(Meevo.Components.Common.SecurityKeys.INTEGRATION_TIPPY_INTERNAL)
                .then(res => res === Model.SecurityAccessEnum.Grant);
        }

        public isTippyConfigured(): ng.IPromise<boolean> {
            return this.checkModule().then(isModuleEnabled => {
                if (!isModuleEnabled) {
                    return false;
                }

                return this.dataService.getSection('', 'config').then(res => !!res && res.isTipProcessingEnabled && !!res.accountToken);
            });
        }

        public isTippyKioskMode(drawerId: string, tipEmployeeIds: string[], unauthorizedConfig: IUnauthorizedConfig = undefined): ng.IPromise<boolean> {
            return this.getTippyMode(drawerId).then(kioskMode => {
                if (kioskMode === TippyMode.Disabled) {
                    return false;
                }

                if (kioskMode === TippyMode.Kiosk) {
                    return true;
                }

                // Tippy Mode is OFFLINE
                // Every employee is mapped to MeevoTips during onboarding, so any non-house tip employee counts.
                // The mappings call is still made so an unauthorized integration is not treated as mapped.
                return this.fetchTippyEmployeeMappings(unauthorizedConfig).then(result => {
                    if (result.isAvailable && _(tipEmployeeIds).any(id => id !== Guid.empty.toString())) {
                        return this.modalService.alert('MESSAGE_REGISTER_TIPPY_OFFLINE_MODE').then(() => false);
                    }

                    return false;
                });
            });
        }

        getKioskByDrawerId(drawerId: string, timeoutMs?: number): ng.IPromise<Model.TippyKiosk> {
            return this.checkModule().then(isGrant => {
                if (!isGrant) {
                    return undefined;
                }

                return this.dataService.getKioskByDrawerId(drawerId, timeoutMs);
            });
        }

        // If the given terminal is paired (via TippyKioskId) with a Tippy kiosk currently engaged in an active
        // tip session, callers should hold off engaging the terminal (payment, tokenization, pole display)
        // instead of interrupting the kiosk screen. On any failure to check (including a timeout - this is
        // a synchronous, in-the-way pre-flight check, so it must fail open promptly rather than hang),
        // fails open (resolves false).
        isPairedKioskLocked(terminal: { tippyKioskId?: string }, drawerId: string): ng.IPromise<boolean> {
            if (!terminal || !terminal.tippyKioskId || !drawerId) {
                return this.$q.resolve(false);
            }

            return this.getKioskByDrawerId(drawerId, this.lockCheckTimeoutMs)
                .then((kiosk: any) => !!(kiosk && kiosk.lockedByUserId && kiosk.deviceId &&
                    kiosk.deviceId.toUpperCase() === terminal.tippyKioskId.toUpperCase()))
                .catch(() => false);
        }

        getKioskByDeviceId(deviceId: string, timeoutMs?: number): ng.IPromise<Model.TippyKiosk> {
            return this.checkModule().then(isGrant => {
                if (!isGrant) {
                    return undefined;
                }

                return this.dataService.getKioskByDeviceId(deviceId, timeoutMs);
            });
        }

        // Same lock check as isPairedKioskLocked, but looks the kiosk up directly by device id with no
        // drawer involved - for callers with no open-drawer/register-session context (e.g. Client Data
        // Maintenance's Credit Card tab). On any failure to check (including a timeout), fails open
        // (resolves false).
        isKioskLocked(terminal: { tippyKioskId?: string }): ng.IPromise<boolean> {
            if (!terminal || !terminal.tippyKioskId) {
                return this.$q.resolve(false);
            }

            return this.getKioskByDeviceId(terminal.tippyKioskId, this.lockCheckTimeoutMs)
                .then((kiosk: any) => !!(kiosk && kiosk.lockedByUserId))
                .catch(() => false);
        }

        // Picks the drawer-scoped or kiosk-id-only lock check (whichever applies), and if the paired
        // kiosk is locked, shows the "device in use" alert and rejects - so callers can chain
        // .then(() => <engage the terminal>) and have the decline handled for them. Resolves (not
        // rejects) when the terminal isn't locked.
        // enabled is the AB#140176 hidden kill switch. Register passes its own cached copy (kept on
        // RegisterContext so a whole session reads one consistent snapshot) - everyone else omits it
        // and gets the shared global setting read here directly, instead of every caller re-deriving it.
        checkPairedKioskLock(terminal: { tippyKioskId?: string }, drawerId?: string, enabled?: boolean): ng.IPromise<void> {
            const isEnabled = enabled === undefined
                ? this.appSettings.APP_BUSINFOSETTINGS.registerSettings.enableDualLockBehavior !== false
                : enabled;
            if (!isEnabled) {
                return this.$q.resolve();
            }

            const check = drawerId
                ? this.isPairedKioskLocked(terminal, drawerId)
                : this.isKioskLocked(terminal);

            return check.then(isLocked => {
                if (isLocked) {
                    return this.modalService.alert('MESSAGE_CC_STATUS_TERMINAL_ALREADY_LOCKED', Meevo.Components.Modal.ModalTranslationModes.Translate)
                        .then(() => this.$q.reject());
                }
            });
        }

        hasExternalTip(sale: Model.Sale, excludeRefund: boolean = false) {
            return _(this.getExternalLines(sale)).any(tippyLine => !excludeRefund || !tippyLine.isRefund);
        }

        getExternalLines(sale: Model.Sale) {
            return [...sale.tipLines, ...sale.saleLines].filter(line => !!line.externalTipTransactionId);
        }

        hasExternalTipRefund(sale: Model.Sale) {
            return _(this.getExternalRefundLines(sale)).any();
        }

        getExternalRefundLines(sale: Model.Sale) {
            return this.getExternalLines(sale).filter(tippyLine => tippyLine.isRefund);
        }

        getExternalRefundTransactionId(sale: Model.Sale) {
            const refundLines = this.getExternalRefundLines(sale);
            if (refundLines.length) {
                return refundLines[0].externalTipTransactionId;
            }
            return null;
        }

        updateTipConvenienceFee(sale: Model.Sale, registerContext, chargedFee: number) {
            sale.tipConvenienceFee = chargedFee
            sale.tipConvenienceFeeRefund = this.getRefundTipConvenienceFee(sale);
            registerContext.updateTotal(sale);
            registerContext.recalculateChangeDue(sale);
        }

        getRefundTipConvenienceFee(sale: Model.Sale) {
            return _(sale.tipLines).chain()
                .filter(tipLine => {
                    return tipLine.isRefund && !!tipLine.externalTipTransactionId
                })
                .reduce((sum, tipLine) => this.roundingService.addNormalizedNumbers(sum, tipLine.convenienceFee || 0), 0)
                .value();
        }

        // Every employee is mapped to MeevoTips during onboarding, so an empty mappings response no longer means
        // "unmapped". The call is still made so an unauthorized integration surfaces (unauthorizedConfig) and
        // makes the sale ineligible. A stored external tip stays eligible even if MeevoTips was disabled afterwards.
        public isSaleTippyEligible(sale: Model.Sale, unauthorizedConfig: IUnauthorizedConfig = undefined): ng.IPromise<boolean> {
            if (this.hasExternalTip(sale, true)) {
                return this.$q.resolve(true);
            }

            // The house employee (empty id) is never sent to MeevoTips.
            return this.fetchTippyEmployeeMappings(unauthorizedConfig).then(result =>
                result.isAvailable && _(sale.tipLines).any(tipLine => !tipLine.isRefund && tipLine.employeeId !== Guid.empty.toString()));
        }

        private getCustomErrorMessage(errorMessage, unauthorizedConfig) {
            let message = this.$translate.instant(errorMessage);
            if (unauthorizedConfig.additionalMessage) {
                message += `<br/><br/>${this.$translate.instant(unauthorizedConfig.additionalMessage)}`;
            }
            return message;
        }

        static factory(): Function {
            let svc = (
                dataService,
                tippyDataService,
                $q,
                busyService,
                pushMessageHandlerService,
                securityService,
                roundingService,
                $translate,
                identityService,
                appSettings
            ) => new TippyService(
                dataService,
                tippyDataService,
                $q,
                busyService,
                pushMessageHandlerService,
                securityService,
                roundingService,
                $translate,
                identityService,
                appSettings
            );

            svc.$inject = [
                "meevo.components.common.DataService",
                "meevo.components.common.tippyDataService",
                "$q",
                "meevo.components.modal.ModalService",
                "meevo.components.common.meevoPushMessageHandlerService",
                "meevo.components.common.SecurityService",
                "meevo.components.common.RoundingService",
                "$translate",
                "meevo.components.common.IdentityService",
                "meevo.AppSettings"
            ];

            return svc;
        }
    }

    angular.module("meevo")
        .factory("meevo.plugins.tippy.tippyService", TippyService.factory());
}
